import { Suspense } from 'react';
import {
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
  type DOMExportOutput,
} from 'lexical';

export type SerializedImageNode = Spread<
  { src: string; altText: string; width?: number },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width?: number;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__key);
  }

  static importJSON(data: SerializedImageNode): ImageNode {
    return new ImageNode(data.src, data.altText, data.width);
  }

  static importDOM() {
    return {
      img: () => ({
        conversion: (el: HTMLElement) => {
          const img = el as HTMLImageElement;
          return {
            node: new ImageNode(img.getAttribute('src') ?? '', img.getAttribute('alt') ?? ''),
          };
        },
        priority: 0 as const,
      }),
    };
  }

  constructor(src: string, altText: string, width?: number, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      type: 'image',
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
    };
  }

  exportDOM(): DOMExportOutput {
    const img = document.createElement('img');
    img.src = this.__src;
    img.alt = this.__altText;
    img.style.maxWidth = '100%';
    img.style.borderRadius = '8px';
    img.style.margin = '8px 0';
    img.style.display = 'block';
    return { element: img };
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.style.display = 'block';
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  isInline(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <img
          src={this.__src}
          alt={this.__altText}
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            margin: '8px 0',
            display: 'block',
          }}
        />
      </Suspense>
    );
  }
}

export function $createImageNode(src: string, altText: string, width?: number): ImageNode {
  return new ImageNode(src, altText, width);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}

export const INSERT_IMAGE_COMMAND_TYPE = 'insertImage';
